import { FaroButton, FaroTooltip, grey } from "@faro-lotv/flat-ui";
import { SxProps } from "@mui/system";
import { MouseEvent, useCallback, type JSX } from "react";

type ProjectTreeActionButtonProps = {
  /** Name of the button */
  name: string;

  /** optional tooltip for the button */
  tooltip?: string;

  /** Function called when the button is clicked */
  onClick?(): void;

  /** true if the button should be disabled (visible but user cannot click on it) */
  disabled?: boolean;

  /** Style props for the button */
  sx?: SxProps;
};

/**
 * @returns a button that will be used for actions in the project tree
 */
export function ProjectTreeActionButton({
  name,
  tooltip,
  onClick,
  disabled,
  sx,
}: ProjectTreeActionButtonProps): JSX.Element {
  const handleClick = useCallback(
    (ev: MouseEvent) => {
      if (onClick) {
        ev.stopPropagation();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <FaroTooltip title={tooltip}>
      <FaroButton
        aria-label={name}
        variant="ghost"
        size="xs"
        sx={[
          {
            textTransform: "none",
            color: "inherit",
            fontWeight: 600,
            fontSize: "0.625rem",
            lineHeight: 1,
            px: 0.5,
            py: 0.25,
            minWidth: 0,
            "&:hover": {
              // 10% opacity is 19 in hexadecimal
              backgroundColor: `${grey[500]}19`,
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={handleClick}
        disabled={disabled}
      >
        {name}
      </FaroButton>
    </FaroTooltip>
  );
}
